// ./src/components/features/ErrorReport/ErrorReportProvider.tsx
//
// Mounts ONE feedback drawer for the whole app and exposes `reportError(...)`
// via context. Any <ErrorAlert> can call it, so the "report an error" flow is
// written exactly once here instead of being repeated on every page.

import React, { useState, useCallback, useMemo } from "react";

import {
  ErrorReportContext,
  ErrorReportInfo,
} from "@/contexts/ErrorReportContext/ErrorReportContext";
import { SupportDrawer } from "@/components/features/Support/SupportDrawer";

export interface ErrorReportProviderProps {
  children: React.ReactNode;
}

/** Compose the pre-filled feedback message from the error + where it happened. */
const buildMessage = (info: ErrorReportInfo): string => {
  const intro = `I ran into an error${
    info.page ? ` on the ${info.page} page` : ""
  }.`;
  const where = [
    info.page ? `Page: ${info.page}` : "",
    info.operation ? `Operation: ${info.operation}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  return `${intro}\n\n${where ? `${where}\n\n` : ""}${info.details}`.trim();
};

export const ErrorReportProvider: React.FC<ErrorReportProviderProps> = ({
  children,
}) => {
  const [opened, setOpened] = useState(false);
  const [message, setMessage] = useState("");

  const reportError = useCallback((info: ErrorReportInfo) => {
    setMessage(buildMessage(info));
    setOpened(true);
  }, []);

  const handleClose = useCallback(() => setOpened(false), []);

  const value = useMemo(() => ({ reportError }), [reportError]);

  return (
    <ErrorReportContext.Provider value={value}>
      {children}
      <SupportDrawer
        opened={opened}
        onClose={handleClose}
        initialTopic="bug_report"
        initialMessage={message}
      />
    </ErrorReportContext.Provider>
  );
};
